import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FormFields from './FormFields';
import { useResolvedForm } from './useResolvedForm';
import { answersToSubmit } from './formConditions';
import PromptModal from './PromptModal';
import { useDialog } from './DialogContext';
import { useAuth } from './AuthContext';
import { canManageForms } from './roles';
import { API_BASE_URL } from './config';
import { exportSubmissionToXlsx } from './excelExport';
import { formatSubmittedAt, getDeviceTimezone } from './dateFormat';
import TemplatePickerModal from './templates/TemplatePickerModal';
import { downloadTemplatePdf } from './templates/templatePdf';

const parseSubmissionData = (raw) => {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return raw;
  return {};
};

const SubmissionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { alert } = useDialog();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [form, setForm] = useState(null);
  const [schema, setSchema] = useState({ fields: [], calculations: [] });
  const [originalData, setOriginalData] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [titlePromptOpen, setTitlePromptOpen] = useState(false);
  // The form's templates, which this submission can be printed through.
  const [templates, setTemplates] = useState([]);
  const [choosingTemplate, setChoosingTemplate] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Read back, a submission shows every answer it holds, even one whose
  // condition the form has since changed. Edited, the conditions apply as they
  // would to a fresh one, since what's re-submitted is a fresh one.
  const resolved = useResolvedForm(schema, formData, { keepAnswered: !editing });
  const { calculatedValues, hiddenIds } = resolved;

  // Submissions live under their form now. Roles that can't open a form's
  // submission list (inspectors) go back to the forms list instead.
  const canListSubmissions = canManageForms(user.role);
  const backTo =
    submission && canListSubmissions ? `/forms/${submission.form_id}/submissions` : '/forms';
  const backLabel = backTo === '/forms' ? 'Back to Forms' : 'Back to Submissions';

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const subRes = await axios.get(`${API_BASE_URL}/api/submissions/${id}`);
        if (cancelled) return;
        setSubmission(subRes.data);
        const parsedData = parseSubmissionData(subRes.data.data);
        setOriginalData(parsedData);
        setFormData(parsedData);

        const [formRes, templatesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/forms/${subRes.data.form_id}`),
          // Without templates there's no PDF to offer, and the rest of the
          // page doesn't wait on them.
          axios.get(`${API_BASE_URL}/api/forms/${subRes.data.form_id}/templates`).catch((err) => {
            console.error('Failed to load templates:', err);
            return { data: [] };
          }),
        ]);
        if (cancelled) return;
        setForm(formRes.data);
        const parsedSchema = JSON.parse(formRes.data.schema);
        setSchema(parsedSchema);
        setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403) {
          setError('You do not have permission to view this submission.');
        } else if (err.response?.status === 404) {
          setError('Submission not found.');
        } else {
          setError('Failed to load submission.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleCancelEdit = () => {
    setFormData(originalData);
    setEditing(false);
  };

  const handleResubmit = (e) => {
    e.preventDefault();
    setTitlePromptOpen(true);
  };

  const handleExportXlsx = () =>
    exportSubmissionToXlsx({ schema, formData, calculatedValues, hiddenIds, submission, form });

  const handleDownloadPdf = async (template) => {
    setChoosingTemplate(false);
    setPrinting(true);
    try {
      await downloadTemplatePdf({ template, schema, formData, calculatedValues, hiddenIds, submission, form });
    } catch (err) {
      console.error('Failed to create PDF:', err);
      alert({ title: 'Error', message: 'Failed to create the PDF. Please try again.' });
    } finally {
      setPrinting(false);
    }
  };

  // One template prints straight away; with several, the user picks.
  const handleDownloadPdfClick = () => {
    if (templates.length === 1) handleDownloadPdf(templates[0]);
    else setChoosingTemplate(true);
  };

  const handleResubmitConfirm = async (value) => {
    const title = value.trim();
    setTitlePromptOpen(false);
    setSubmitting(true);
    try {
      const finalData = answersToSubmit(schema, formData, resolved);
      await axios.post(`${API_BASE_URL}/api/submissions`, {
        parent_submission_id: submission.id,
        data: finalData,
        title: title || undefined,
        timezone: getDeviceTimezone() || undefined,
      });
      await alert({ title: 'Success', message: 'Re-submitted successfully!' });
      navigate(backTo);
    } catch (err) {
      console.error('Failed to re-submit:', err);
      alert({ title: 'Error', message: err.response?.data?.msg || 'Failed to re-submit. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
        <div className="max-w-2xl mx-auto px-4 text-center text-white">Loading submission...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="text-red-300 mb-4">{error}</div>
          <button
            onClick={() => navigate(backTo)}
            className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-4 py-2 rounded-lg border border-gray-600"
          >
            {backLabel}
          </button>
        </div>
      </div>
    );
  }

  if (!submission || !form) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <PromptModal
        open={titlePromptOpen}
        title="Re-submit form"
        message="Give this re-submission a title to help you find it later. Leave blank to skip."
        defaultValue={submission.title || form.name}
        placeholder="Submission title"
        confirmLabel="Re-submit"
        onConfirm={handleResubmitConfirm}
        onCancel={() => setTitlePromptOpen(false)}
      />
      <TemplatePickerModal
        open={choosingTemplate}
        templates={templates}
        onChoose={handleDownloadPdf}
        onCancel={() => setChoosingTemplate(false)}
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(backTo)}
            className="inline-flex bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            ← {backLabel}
          </button>
        </div>

        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">
            {submission.title || form.name}
          </h1>
          {submission.title && (
            <p className="text-sm text-gray-400">{form.name}</p>
          )}
          {form.description && (
            <p className="text-lg text-gray-400">{form.description}</p>
          )}
          <div className="mt-3 text-sm text-gray-400 flex items-center justify-center gap-3 flex-wrap">
            {submission.user_email && <span>Submitted by {submission.user_email}</span>}
            <span>•</span>
            <span>{formatSubmittedAt(submission.submitted_at, submission.submitted_tz)}</span>
            {submission.parent_submission_id && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                Re-submission
              </span>
            )}
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
          <form onSubmit={handleResubmit}>
            <FormFields
              schema={schema}
              formData={formData}
              calculatedValues={calculatedValues}
              hiddenIds={hiddenIds}
              onChange={handleInputChange}
              disabled={!editing}
            />

            <div className="mt-8 flex flex-wrap justify-end gap-4">
              <button
                type="button"
                onClick={handleExportXlsx}
                disabled={submitting}
                className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 border border-gray-600 hover:border-gray-500 disabled:opacity-50"
              >
                Export Excel
              </button>
              {templates.length > 0 && (
                <button
                  type="button"
                  onClick={handleDownloadPdfClick}
                  disabled={submitting || printing}
                  className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 border border-gray-600 hover:border-gray-500 disabled:opacity-50"
                >
                  {printing ? 'Creating PDF...' : 'Download PDF'}
                </button>
              )}
              {!editing ? (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={submitting}
                    className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 border border-gray-600 hover:border-gray-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Re-submitting...' : 'Re-submit'}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmissionView;
